/**
 * TopBar — phase badge + connection status + back/leave button.
 *
 * Sticky at the top of the viewport (56pt height per wireframe §Layout).
 * Phase badge reads from the engine TurnPhase type.
 * ConnectionPill is embedded here for the client (no standalone component needed).
 */

import type { TurnPhase } from "@cloudnest/cascadia-engine";

export interface TopBarProps {
  readonly phase: TurnPhase;
  readonly activeSeat: number;
  readonly totalPlayers: number;
  readonly connectionStatus: "connected" | "reconnecting";
  readonly onLeave: () => void;
}

const PHASE_LABEL: Record<TurnPhase, string> = {
  marketCheck: "시장 점검 / Market Check",
  placeTile: "타일 배치 / Place Tile",
  placeWildlife: "야생동물 배치 / Place Wildlife",
  unlockLandmark: "랜드마크 / Landmark",
  endTurn: "턴 종료 / End Turn",
  scoring: "점수 계산 / Scoring",
};

export function TopBar({
  phase,
  activeSeat,
  totalPlayers,
  connectionStatus,
  onLeave,
}: TopBarProps) {
  const isConnected = connectionStatus === "connected";

  return (
    <header
      data-testid="cascadia-top-bar"
      data-active-seat={activeSeat}
      data-total-players={totalPlayers}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        height: 56,
        background: "var(--cn-cascadia-bg)",
        borderBottom: "1px solid var(--cn-cascadia-tile-outline)",
        position: "sticky",
        top: 0,
        zIndex: 10,
        boxSizing: "border-box",
        gap: 8,
      }}
    >
      <button
        data-testid="cascadia-leave-btn"
        onClick={onLeave}
        style={{
          minWidth: 44,
          minHeight: 44,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--cn-cascadia-label)",
          fontSize: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        aria-label="Leave game"
        type="button"
      >
        &#8592;
      </button>

      <div
        data-testid="cascadia-phase-badge"
        data-phase={phase}
        style={{
          background: "var(--cn-cascadia-phase-bg)",
          color: "var(--cn-cascadia-phase-text)",
          border: "1px solid var(--cn-cascadia-phase-border)",
          borderRadius: 8,
          padding: "4px 12px",
          fontSize: 13,
          fontWeight: 600,
          textAlign: "center",
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {PHASE_LABEL[phase]}
      </div>

      <div
        style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}
      >
        <div
          role="status"
          data-testid="connection-status"
          data-status={connectionStatus}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: isConnected
              ? "var(--cn-cascadia-valid-dest)"
              : "var(--cn-cascadia-corridor-active)",
            fontWeight: 600,
          }}
          aria-label={isConnected ? "Connected" : "Reconnecting…"}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isConnected
                ? "var(--cn-cascadia-valid-dest)"
                : "var(--cn-cascadia-corridor-active)",
              display: "inline-block",
            }}
          />
          {isConnected ? "●" : "…"}
        </div>
        <div
          data-testid="cascadia-turn-counter"
          style={{
            color: "var(--cn-cascadia-label)",
            fontSize: 12,
            minWidth: 44,
            textAlign: "right",
          }}
        >
          P{activeSeat + 1}/{totalPlayers}
        </div>
      </div>
    </header>
  );
}
